import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import PolicyPage, { PolicyContactBox } from "./PolicyPage";
import "../css/terms.scss";

export default function Terms() {
  return (
    <PolicyPage
      activePath="/terms"
      eyebrow="Legal"
      title="Terms of sale & use"
      intro="The terms for browsing this storefront and placing an order with ottireluna."
      note={{
        title: "Please read before ordering",
        content:
          "These terms operate alongside the policies linked on this site. Mandatory consumer protections in your location take priority over any conflicting term.",
      }}
      sections={[
        {
          id: "terms-scope",
          shortTitle: "Scope & seller",
          title: "Scope, seller & acceptance",
          content: (
            <>
              <p>
                These terms apply to personal use of the {siteConfig.brandName} website
                and purchases made through it. The currently configured seller contact is
                {` ${siteConfig.contact.email}`}; available operator details appear in the
                <Link to="/legal-notice"> legal notice</Link>.
              </p>
              <p>
                By using the site, you agree to these website rules. By submitting an order,
                you make an offer to purchase under the terms and policies presented at
                checkout. If you do not agree, do not place the order.
              </p>
            </>
          ),
        },
        {
          id: "terms-use",
          shortTitle: "Using the site",
          title: "Eligibility & acceptable use",
          content: (
            <>
              <p>
                A purchaser must have legal capacity to contract and authority to use the
                selected payment method. Information supplied for an order must be current,
                complete and accurate.
              </p>
              <p>
                You must not interfere with security, introduce malicious code, bypass
                access controls, place fraudulent orders, harvest personal data, scrape at
                a harmful rate, misuse discounts or use site content to mislead others.
                Reasonable access may be limited to protect customers and the service.
              </p>
            </>
          ),
        },
        {
          id: "terms-products",
          shortTitle: "Products & prices",
          title: "Products, availability & prices",
          content: (
            <>
              <p>
                We aim to describe products accurately. Screens and lighting may affect
                colour, while handmade or natural materials may show minor variation. Check
                measurements, materials and care information before ordering.
              </p>
              <p>
                Stock is not reserved merely by adding an item to a basket. Prices use
                {` ${siteConfig.currency.code}`} and may change before an order is accepted.
                Delivery, destination tax or import charges are shown or explained
                separately. Obvious errors may be corrected as described in the
                <Link to="/orders"> orders policy</Link>.
              </p>
            </>
          ),
        },
        {
          id: "terms-orders",
          shortTitle: "Orders & payment",
          title: "Order formation & payment",
          content: (
            <>
              <p>
                An acknowledgement confirms that the order request was received; acceptance
                occurs when payment is accepted and an order confirmation is issued, unless
                applicable law provides otherwise. We may decline or cancel before dispatch
                for unavailable stock, failed authorisation, an unsupported destination,
                obvious error, suspected fraud or a legal restriction.
              </p>
              <p>
                Payment is handled through a hosted provider. The
                <Link to="/payments"> payments and security policy</Link> explains
                authorisation, currency and safer checkout. We will not request full card
                details by email.
              </p>
            </>
          ),
        },
        {
          id: "terms-fulfilment",
          shortTitle: "Delivery & returns",
          title: "Delivery, cancellation, returns & faults",
          content: (
            <>
              <p>
                Dispatch windows and delivery estimates are described in the
                <Link to="/shipping"> shipping policy</Link>. Risk passes at the point
                required by applicable law. The customer is responsible for an accurate,
                accessible address and for responding to lawful carrier or customs requests.
              </p>
              <p>
                Change-of-mind returns and refunds follow the
                <Link to="/returns"> returns policy</Link>. Faulty, unsafe or misdescribed
                products are handled under applicable consumer guarantees and the
                <Link to="/warranty"> product care and warranty policy</Link>.
              </p>
            </>
          ),
        },
        {
          id: "terms-content",
          shortTitle: "Content & third parties",
          title: "Intellectual property & external services",
          content: (
            <>
              <p>
                Site material may be protected by rights belonging to {siteConfig.brandName}
                or third parties. Personal shopping use does not grant a right to republish,
                sell, imitate or commercially exploit it. See the
                <Link to="/intellectual-property"> intellectual property policy</Link>,
                including the process for reporting rights concerns.
              </p>
              <p>
                External payment, carrier or linked services operate under their own terms.
                References to third-party names identify compatibility and do not imply
                sponsorship or endorsement.
              </p>
            </>
          ),
        },
        {
          id: "terms-liability",
          shortTitle: "Liability",
          title: "Service availability & liability",
          content: (
            <>
              <p>
                We take reasonable care to keep the site and product information available,
                but maintenance, networks and third-party services can cause interruption.
                To the extent permitted by law, we are not responsible for losses that were
                not reasonably foreseeable when a contract was formed or for business losses
                arising from a consumer purchase.
              </p>
              <p>
                Nothing limits liability for fraud, wilful misconduct, death or personal
                injury caused by negligence, breach of mandatory consumer protections, or
                any liability that cannot lawfully be limited.
              </p>
            </>
          ),
        },
        {
          id: "terms-general",
          shortTitle: "General terms",
          title: "Changes, severability, disputes & contact",
          content: (
            <>
              <p>
                The version accepted with an order governs that order. Website-use terms
                may be updated prospectively. If one provision is unenforceable, the rest
                continue as far as law permits; a delay in enforcing a right is not a waiver.
              </p>
              <p>
                No exclusive governing law or forum is stated because the project does not
                currently identify a registered operator or final target markets. Mandatory
                rights in a customer’s habitual residence remain available. Please use the
                <Link to="/complaints"> complaints process</Link> first when possible.
              </p>
              <PolicyContactBox label="Question about these terms?" />
            </>
          ),
        },
      ]}
    />
  );
}
